import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { nanoid } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { isEmpty } from "lodash";
import {
  useAppDispatch,
  useAppSelector,
} from "../redux-store/CustomReduxHooks";
import {
  setDeviceType,
  setIpAddress,
  setUserAgentDetails,
  updateTraceId,
} from "../redux-store/reducers/CsAppSlice";
import { setLanguage } from "../redux-store/reducers/LanguageSlice";
import { getBrowserInfo, getModuleNameFromPath } from "../utils/helper";
import { ipifyClientsIp } from "../utils/ApiEndpoints";
import CsSessionStorageUtils from "../utils/CsSessionStorageUtils";
import { downloadDocumentCode, platformWeb } from "../utils/constants";
import { initTwaDownloadListener } from "../utils/twaDownloadBridge";
import useDeviceDetection from "./useDeviceDetection";
import { useSaveUIException } from "./useSaveUIException";
declare global {
  interface Window {
    plugSDK?: {
      init: (config: any) => void;
    };
    SessionReplay?:
       {
          getInstance?: () => {
            identify?: (userId: string) => void;
            track?: (event: string, payload?: any) => void;
          };
        }
  }
}

const runtimeWindow = globalThis as any;

const useCsAppBootstrapContext = () => {
  const appDispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { triggerSaveUIException } = useSaveUIException();
  const isPwaMobileOrWeb = useDeviceDetection();
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);

  const isSuperApp = csAppSlice.channel === "SUPER APP";
  const finalTraceId =
    CsSessionStorageUtils.get("TRACE_ID") || csAppSlice.traceId || "";
  const csUserId =
    csAppSlice.csUserId || csAppSlice?.userDetails?.CS_USER_ID || "";

  useEffect(() => {
    if (isEmpty(CsSessionStorageUtils.get("TRACE_ID"))) {
      const randomTraceId = "CS" + nanoid(14);
      appDispatch(updateTraceId(randomTraceId));
      CsSessionStorageUtils.set("TRACE_ID", randomTraceId);
    }
  }, []);

  useEffect(() => {
    const cleanup = initTwaDownloadListener(
      csAppSlice,
      appDispatch,
      downloadDocumentCode,
    );

    return () => {
      cleanup?.();
    };
  }, []);

  useEffect(() => {
    if (userDetails?.preferredLanguage) {
      appDispatch(setLanguage(userDetails.preferredLanguage));
    }
  }, [userDetails?.preferredLanguage]);

  useEffect(() => {
    const appId = process.env.REACT_APP_DEVREV_APP_ID || "";
    if (runtimeWindow?.plugSDK && !isSuperApp) {
      runtimeWindow?.plugSDK?.init({
        app_id: appId,
        disable_plug_chat_window: true,
        identity: {
          user_ref: finalTraceId,
          user_traits: {
            display_name: finalTraceId,
          },
        },
      });
    }
  }, []);

  useEffect(() => {
    const replay = window?.SessionReplay?.getInstance?.();

    if (replay && !isEmpty(csUserId)) {
      replay.identify?.(csUserId);
      replay.track?.("session_started", {
        traceId: csUserId,
        path: pathname,
      });
    }
  }, [csUserId]);

  useEffect(() => {
    window?.SessionReplay?.getInstance?.()?.track?.("page_view", {
      path: pathname,
    });
  }, [pathname]);

  useEffect(() => {
    if (!isEmpty(CsSessionStorageUtils.get("TRACE_ID"))) {
      const randomTraceId = CsSessionStorageUtils.get("TRACE_ID");
      appDispatch(updateTraceId(randomTraceId));
    }
    const browserInfo = getBrowserInfo();
    appDispatch(
      setUserAgentDetails({
        platform: platformWeb,
        deviceId: "",
        deviceType: platformWeb,
        browserName: browserInfo.browserName,
        browserVersion: browserInfo.fullVersion,
      }),
    );
  }, []);

  useEffect(() => {
    axios
      .get(ipifyClientsIp)
      .then((r: AxiosResponse) => r?.data?.ip)
      .then((ip) => {
        appDispatch(setIpAddress(ip || ""));
      })
      .catch((error) => {
        const currentPath = window?.location?.pathname;
        triggerSaveUIException({
          moduleName: getModuleNameFromPath(currentPath),
          pageName: currentPath || "",
          statusCode: 500,
          uiDisplayMessage:
            "fileName(CsApp.tsx) and functionName(get(ipifyClientsIp)) ",
          errorMessage: {
            stk: error.stack || "No stack available",
            msg: error.message || "Unknown error occurred",
          },
        });
      });
  }, []);

  useEffect(() => {
    if (isPwaMobileOrWeb && isPwaMobileOrWeb.length > 0) {
      CsSessionStorageUtils.set("DEVICE_TYPE", isPwaMobileOrWeb);
      appDispatch(setDeviceType(isPwaMobileOrWeb));
    }
  }, [isPwaMobileOrWeb]);
};

export default useCsAppBootstrapContext;
